import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { ChevronDown, ChevronUp, Lock, Unlock, Download, CheckCircle, AlertCircle, Circle, Save, ThumbsUp, 
ThumbsDown, Search, FileCheck, FileX, FileWarning, Network } from 'lucide-react';
import { Toast } from '../components/ui/Toast';
import useAppreciationsConfig from '../hooks/useAppreciationsConfig';

const TableauNotes = ({ eleves, matieres, trimestre, annee_scolaire_id, classeId, onSave }) => {
  // États existants
  const [notesData, setNotesData] = useState({});
  const [notesEnrichies, setNotesEnrichies] = useState([]);
  const [loading, setLoading] = useState(true);
  const MAX_NOTES = 3;

  // Nouveaux états

  const [savingEleve, setSavingEleve] = useState(null);
  const [eleveSearchTerm, setEleveSearchTerm] = useState('');
  const [openEleves, setOpenEleves] = useState({});
  const [modificationEnabled, setModificationEnabled] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [saving, setSaving] = useState(false);
  const [classeInfo, setClasseInfo] = useState(null);
  const [appreciationsGenerales, setAppreciationsGenerales] = useState({});
  const [enseignantsParMatiere, setEnseignantsParMatiere] = useState({});
  const [matieresDynamiques, setMatieresDynamiques] = useState([]);  // ⬅️ NOUVEAU
  const { getAppreciation } = useAppreciationsConfig();
  const [configChamps, setConfigChamps] = useState({
  observation_matiere: true,
  appreciation_responsable: true,
  appreciation_directeur: true,
  observation_parents: true
});
  const [statsClasse, setStatsClasse] = useState({
    moyenne_plus_elevee: '-',
    moyenne_plus_basse: '-',
    eleve_moyenne_plus_elevee: null,
    eleve_moyenne_plus_basse: null
  });
  const [statsAnnuelles, setStatsAnnuelles] = useState({
    par_eleve: {},
    stats_classe: null
  });
  const [showElevesDossiersIncomplets, setShowElevesDossiersIncomplets] = useState(false);



  /**
 * Parse les notes de manière sécurisée
 * Gère tous les cas : string JSON, array déjà parsé, valeurs nulles, erreurs
 * @param {*} notes - Les notes à parser (string JSON ou array)
 * @param {number} eleveId - ID de l'élève (pour les logs)
 * @param {number} matiereId - ID de la matière (pour les logs)
 * @returns {Array} - Tableau de notes ou tableau vide en cas d'erreur
 */
const parseNotesSecure = (notes, eleveId = null, matiereId = null) => {
  try {
    // Cas 1: Valeur nulle ou undefined
    if (notes === null || notes === undefined) {
      return [];
    }
    
    // Cas 2: Déjà un array (retour direct)
    if (Array.isArray(notes)) {
      return notes;
    }
    
    // Cas 3: String à parser
    if (typeof notes === 'string') {
      const trimmed = notes.trim();
      
      // String vide ou array vide
      if (trimmed === '' || trimmed === '[]') {
        return [];
      }
      
      // Parser le JSON
      const parsed = JSON.parse(trimmed);
      
      // Vérifier que le résultat est bien un array
      if (Array.isArray(parsed)) {
        return parsed;
      }
      
      // Si ce n'est pas un array après parsing, retourner vide
      console.warn(`⚠️ Notes parsées invalides (élève: ${eleveId}, matière: ${matiereId}):`, parsed);
      return [];
    }
    
    // Cas 4: Type inattendu
    console.warn(`⚠️ Type de notes inattendu (élève: ${eleveId}, matière: ${matiereId}):`, typeof notes);
    return [];
    
  } catch (error) {
    // En cas d'erreur de parsing, logger et retourner vide
    console.error(`❌ Erreur parsing notes (élève: ${eleveId}, matière: ${matiereId}):`, error.message);
    return [];
  }
};


  // Charger les notes enrichies au montage du composant

  // ========================================
// useEffect 1 : Charger TOUTES les données en 1 fois
// ========================================
useEffect(() => {
  const chargerDonneesCompletes = async () => {
    try {
      console.log('🔄 Début chargement données complètes...', { classeId, annee_scolaire_id });
      
      if (!classeId || !annee_scolaire_id) {
        console.warn('⚠️ Paramètres manquants:', { classeId, annee_scolaire_id });
        setLoading(false);
        return;
      }

      // ⬅️ NE charger qu'UNE SEULE FOIS par classe
      if (notesEnrichies.length > 0 && notesEnrichies[0]?.classe_id === parseInt(classeId)) {
        console.log('✅ Données déjà en cache, pas de rechargement');
        setLoading(false);
        return;
      }

      setLoading(true);
      
      // ⬅️ 1 SEULE REQUÊTE pour tout charger
      const result = await api.scolarite.getNotesComplet(
        classeId, 
        annee_scolaire_id
      );
      
      console.log('📦 Résultat getNotesComplet:', result);
      

      if (result.success && result.data) {
      const { notes, stats, appreciations, classe_info, enseignants, matieres: matieresBackend, annuel } = result.data;  // ⬅️ AJOUTER matieres
      
      console.log('✅ Succès!');
      console.log(`  → ${notes.length} notes (tous trimestres)`);
      console.log(`  → ${appreciations.length} appréciations (tous trimestres)`);
      console.log('  → Stats:', stats);
      console.log(`  → ${matieresBackend?.length || 0} matières avec coefficients`);  // ⬅️ AJOUTER
      
      // Stocker TOUTES les notes (tous trimestres)
      setNotesEnrichies(notes);
      setClasseInfo(classe_info);

      // ⬅️ NOUVEAU : Stocker directement les matières avec coefficients du backend
      if (matieresBackend && matieresBackend.length > 0) {
        console.log('📚 Matières du backend:', matieresBackend);
        setMatieresDynamiques(matieresBackend);
      } else {
        console.warn('⚠️ Aucune matière retournée par le backend');
      }


        // Stocker les enseignants par matière
        if (enseignants && enseignants.length > 0) {
          const enseignantsMap = {};
          enseignants.forEach(ens => {
            enseignantsMap[ens.matiere_id] = ens.professeur_nom;
          });
          setEnseignantsParMatiere(enseignantsMap);
          console.log('👨‍🏫 Enseignants chargés:', enseignantsMap);
        }
        
        // Stocker les stats de TOUS les trimestres dans le state
        window.statsParTrimestre = stats;
        window.statsAnnuellesClasse = annuel || { par_eleve: {}, stats_classe: null };
        setStatsAnnuelles(window.statsAnnuellesClasse);
        
        // Mettre à jour les stats du trimestre actuel
        if (stats[trimestre]) {
          setStatsClasse(stats[trimestre]);
        }
        
        // Stocker TOUTES les appréciations dans le state
        window.toutesLesAppreciations = appreciations;
        
        // Filtrer les notes du trimestre actuel pour le pré-remplissage
        const notesDuTrimestre = notes.filter(
          note => note.trimestre === parseInt(trimestre)
        );
        
        console.log(`✅ Notes du trimestre ${trimestre}:`, notesDuTrimestre.length);
        
        // Pré-remplir notesData
        const prefilledData = {};
        notesDuTrimestre.forEach(note => {
          if (!prefilledData[note.eleve_id]) {
            prefilledData[note.eleve_id] = {};
          }
          
          prefilledData[note.eleve_id][note.matiere_id] = {
            notes: parseNotesSecure(note.notes, note.eleve_id, note.matiere_id),
            moyenne: note.moyenne,
            appreciation: note.appreciation || ''
          };
        });
        
        setNotesData(prefilledData);
        
        // Filtrer les appréciations du trimestre actuel
        const appreciationsDuTrimestre = appreciations.filter(
          app => app.trimestre === parseInt(trimestre)
        );
        
        const prefilledAppreciationsGenerales = {};
        appreciationsDuTrimestre.forEach(app => {
          prefilledAppreciationsGenerales[app.eleve_id] = {
            appreciation_conseil_classe: app.appreciation_conseil_classe || '',
            appreciation_directeur: app.appreciation_directeur || '',
            observation_parents: app.observation_parents || ''
          };
        });
        
        setAppreciationsGenerales(prefilledAppreciationsGenerales);
        
        console.log('📝 Notes pré-remplies:', prefilledData);
        console.log('💬 Appréciations pré-remplies:', prefilledAppreciationsGenerales);
        
      } else {
        console.error('❌ Échec:', result.error);
      }
    } catch (error) {
      console.error('❌ Exception chargement données complètes:', error);
    } finally {
      console.log('🏁 Fin chargement, setLoading(false)');
      setLoading(false);
    }
  };

  chargerDonneesCompletes();
}, [classeId, annee_scolaire_id]); // ⬅️ NE DÉPEND PLUS DU TRIMESTRE !



// ========================================
// useEffect 2 : Changer de trimestre INSTANTANÉMENT
// ========================================
useEffect(() => {
  if (notesEnrichies.length === 0) {
    console.log('⚠️ Pas encore de notes chargées, skip');
    return;
  }
  
  console.log('🔄 Changement de trimestre vers:', trimestre);
  console.log('📊 Total notes enrichies:', notesEnrichies.length);
  
  // Mettre à jour les stats du nouveau trimestre
  if (window.statsParTrimestre && window.statsParTrimestre[trimestre]) {
    setStatsClasse(window.statsParTrimestre[trimestre]);
    console.log('📊 Stats mises à jour:', window.statsParTrimestre[trimestre]);
  } else {
    console.warn('⚠️ Pas de stats pour le trimestre', trimestre);
  }
  
  // Filtrer les notes du nouveau trimestre
  const notesDuTrimestre = notesEnrichies.filter(
    note => parseInt(note.trimestre) === parseInt(trimestre)
  );
  
  console.log(`✅ ${notesDuTrimestre.length} notes trouvées pour le trimestre ${trimestre}`);
  
  if (notesDuTrimestre.length > 0) {
    console.log('📝 Première note:', notesDuTrimestre[0]);
  }
  
  // Pré-remplir notesData avec les notes du nouveau trimestre
  const prefilledData = {};
  notesDuTrimestre.forEach(note => {
    if (!prefilledData[note.eleve_id]) {
      prefilledData[note.eleve_id] = {};
    }
    
    // ⬅️ CRITIQUE : Parser les notes correctement
    const notesArray = parseNotesSecure(note.notes, note.eleve_id, note.matiere_id);
    
    prefilledData[note.eleve_id][note.matiere_id] = {
      notes: notesArray,
      moyenne: note.moyenne || '0',
      appreciation: note.appreciation || ''
    };
  });
  
  console.log('📝 Notes pré-remplies:', Object.keys(prefilledData).length, 'élèves');
  
  setNotesData(prefilledData);
  
  // Filtrer les appréciations du nouveau trimestre
  if (window.toutesLesAppreciations) {
    const appreciationsDuTrimestre = window.toutesLesAppreciations.filter(
      app => parseInt(app.trimestre) === parseInt(trimestre)
    );
    
    console.log(`💬 ${appreciationsDuTrimestre.length} appréciations trouvées pour le trimestre ${trimestre}`);
    
    const prefilledAppreciationsGenerales = {};
    appreciationsDuTrimestre.forEach(app => {
      prefilledAppreciationsGenerales[app.eleve_id] = {
        appreciation_conseil_classe: app.appreciation_conseil_classe || '',
        appreciation_directeur: app.appreciation_directeur || '',
        observation_parents: app.observation_parents || ''
      };
    });
    
    setAppreciationsGenerales(prefilledAppreciationsGenerales);
    console.log('💬 Appréciations mises à jour:', Object.keys(prefilledAppreciationsGenerales).length, 'élèves');
  } else {
    console.warn('⚠️ Pas d\'appréciations chargées');
  }
  
}, [trimestre, notesEnrichies]); // ⬅️ AJOUTER notesEnrichies dans les dépendances




// ========================================
// useEffect 3 : Détecter les changements de coefficients
// ========================================
useEffect(() => {
  if (matieresDynamiques.length === 0 || !matieres) return;
  
  console.log('🔍 Vérification des coefficients...');
  
  // Comparer les coefficients entre matieres (props) et matieresDynamiques (state)
  let coefficientsChanges = false;
  
  matieresDynamiques.forEach(matiereDyn => {
    const matiereProps = matieres.find(m => m.id === matiereDyn.id);
    
    if (matiereProps && matiereProps.coefficient !== matiereDyn.coefficient) {
      console.log(`🔄 Coefficient changé pour ${matiereDyn.nom}:`);
      console.log(`   Ancien: ${matiereDyn.coefficient}`);
      console.log(`   Nouveau: ${matiereProps.coefficient}`);
      coefficientsChanges = true;
    }
  });
  
  // Si des coefficients ont changé, recharger les données
  if (coefficientsChanges) {
    console.log('🔄 Rechargement des données pour mise à jour des coefficients...');
    
    const recharger = async () => {
      setLoading(true);
      
      const result = await api.scolarite.getNotesComplet(
        classeId, 
        annee_scolaire_id
      );
      
      if (result.success && result.data) {
        const { notes, stats, appreciations, classe_info, enseignants, matieres: matieresBackend, annuel } = result.data;  // ⬅️ AJOUTER matieres
        
        setNotesEnrichies(notes);
        setClasseInfo(classe_info);
        
        // ⬅️ MODIFIÉ : Utiliser les matières du backend SI DISPONIBLES
        if (matieresBackend && matieresBackend.length > 0) {
          console.log(`📚 ${matieresBackend.length} matières avec coefficients mis à jour depuis le backend`);
          setMatieresDynamiques(matieresBackend);
        } else {
          // Fallback : Extraire depuis les notes
          console.log('⚠️ Pas de matières du backend, extraction depuis les notes...');
          const matieresAvecCoefficients = {};
          notes.forEach(note => {
            if (!matieresAvecCoefficients[note.matiere_id]) {
              matieresAvecCoefficients[note.matiere_id] = {
                id: note.matiere_id,
                nom: note.matiere_nom,
                coefficient: note.coefficient,
                coefficient_defaut: note.coefficient_defaut,
                coefficient_specifique: note.coefficient_specifique
              };
            }
          });
          
          setMatieresDynamiques(Object.values(matieresAvecCoefficients));
        }
        
        if (enseignants && enseignants.length > 0) {
          const enseignantsMap = {};
          enseignants.forEach(ens => {
            enseignantsMap[ens.matiere_id] = ens.professeur_nom;
          });
          setEnseignantsParMatiere(enseignantsMap);
        }
        
        window.statsParTrimestre = stats;
        window.statsAnnuellesClasse = annuel || { par_eleve: {}, stats_classe: null };
        setStatsAnnuelles(window.statsAnnuellesClasse);
        if (stats[trimestre]) {
          setStatsClasse(stats[trimestre]);
        }
        
        window.toutesLesAppreciations = appreciations;
        
        const notesDuTrimestre = notes.filter(
          note => parseInt(note.trimestre) === parseInt(trimestre)
        );
        
        const prefilledData = {};
        notesDuTrimestre.forEach(note => {
          if (!prefilledData[note.eleve_id]) {
            prefilledData[note.eleve_id] = {};
          }
          
          prefilledData[note.eleve_id][note.matiere_id] = {
            notes: parseNotesSecure(note.notes, note.eleve_id, note.matiere_id),
            moyenne: note.moyenne,
            appreciation: note.appreciation || ''
          };
        });
        
        setNotesData(prefilledData);
        
        const appreciationsDuTrimestre = appreciations.filter(
          app => parseInt(app.trimestre) === parseInt(trimestre)
        );
        
        const prefilledAppreciationsGenerales = {};
        appreciationsDuTrimestre.forEach(app => {
          prefilledAppreciationsGenerales[app.eleve_id] = {
            appreciation_conseil_classe: app.appreciation_conseil_classe || '',
            appreciation_directeur: app.appreciation_directeur || '',
            observation_parents: app.observation_parents || ''
          };
        });
        
        setAppreciationsGenerales(prefilledAppreciationsGenerales);
        
        setToast({ 
          show: true, 
          message: '✅ Coefficients mis à jour automatiquement', 
          type: 'success' 
        });
        
        console.log('✅ Rechargement terminé avec nouveaux coefficients');
      }
      
      setLoading(false);
    };
    
    recharger();
  }
}, [matieres]);  // ⬅️ Dépendance sur matieres (props)




useEffect(() => {
  const chargerConfigChamps = async () => {
    try {
      const result = await api.notesVerrouillage.getConfigChamps();
      if (result.success) {
        // Déterminer la section depuis la classe sélectionnée
        const sectionNom = classeInfo?.section_nom?.toLowerCase() || 'college';
        const sectionConfig = result.data.find(r => r.section === sectionNom) 
          || result.data.find(r => r.section === 'college');
        if (sectionConfig) {
          setConfigChamps({
            observation_matiere: sectionConfig.observation_matiere === 1,
            appreciation_responsable: sectionConfig.appreciation_responsable === 1,
            appreciation_directeur: sectionConfig.appreciation_directeur === 1,
            observation_parents: sectionConfig.observation_parents === 1,
          });
        }
      }
    } catch (err) {
      console.error('❌ Erreur chargement config champs:', err);
    }
  };
  chargerConfigChamps();
}, [classeInfo]);




  // Fonctions de calcul existantes
  const calculerMoyenne = (notes) => {
    const validNotes = notes.filter(n => n !== null && n !== '');
    if (validNotes.length === 0) return null;
    return (validNotes.reduce((a, b) => a + b, 0) / validNotes.length).toFixed(2);
  };

  const calculerMoyenneGenerale = (eleveId) => {
    let moyennes = [];
    matieres.forEach(matiere => {
      const moyenne = notesData[eleveId]?.[matiere.id]?.moyenne;
      if (moyenne) moyennes.push(parseFloat(moyenne));
    });
    if (moyennes.length === 0) return null;
    return (moyennes.reduce((a, b) => a + b, 0) / moyennes.length).toFixed(2);
  };

  // Nouvelles fonctions utilitaires
  const toggleEleve = (eleveId) => {
    setOpenEleves(prev => ({
      ...prev,
      [eleveId]: !prev[eleveId]
    }));
  };


  const getInfosEnrichies = (eleveId, matiereId) => {
  const noteEnrichie = notesEnrichies.find(
    n => parseInt(n.eleve_id) === parseInt(eleveId) && 
         parseInt(n.matiere_id) === parseInt(matiereId) && 
         parseInt(n.trimestre) === parseInt(trimestre)
  );
  return noteEnrichie || {};
};

  const getStatutEleve = (eleveId) => {
    if (!matieres) return 'empty';
    let notesCount = 0;
    let notesVerrouilleesCount = 0;
    
    matieres.forEach(matiere => {
      const notes = notesData[eleveId]?.[matiere.id]?.notes || [];
      const infos = getInfosEnrichies(eleveId, matiere.id);
      
      if (notes.some(n => n !== null && n !== '')) {
        notesCount++;
        if (infos.verrouille === 1) {
          notesVerrouilleesCount++;
        }
      }
    });

    if (notesCount === 0) return 'empty';
    if (notesCount < matieres.length) return 'partial';
    if (notesVerrouilleesCount === matieres.length) return 'complete-locked';
    return 'complete';
  };

  const calculerClassement = (eleveId) => {
    if (!eleves || eleves.length === 0) {
      return { position: 0, total: 0 };
    }
    const moyennesEleves = eleves.map(eleve => ({
      id: eleve.id,
      moyenne: parseFloat(calculerMoyenneGenerale(eleve.id) || 0)
    })).filter(e => e.moyenne > 0);

    moyennesEleves.sort((a, b) => b.moyenne - a.moyenne);

    const position = moyennesEleves.findIndex(e => e.id === eleveId) + 1;
    const total = moyennesEleves.length;

    return { position, total };
  };



  const isTroisiemeTrimestre = parseInt(trimestre, 10) === 3;

  const formatNoteAnnuelle = (value) => {
    const note = parseFloat(value);
    return Number.isNaN(note) ? '-' : note.toFixed(2);
  };

  const getBilanAnnuelEleve = (eleveId) => {
    return statsAnnuelles?.par_eleve?.[eleveId] || statsAnnuelles?.par_eleve?.[String(eleveId)] || null;
  };

  const getTrimestresBilanManquants = (bilan) => {
    return [1, 2, 3].filter((trimestreKey) => {
      const moyenne = parseFloat(bilan?.moyennes_trimestrielles?.[trimestreKey]);
      return Number.isNaN(moyenne) || moyenne <= 0;
    });
  };

  const getElevesBilanAnnuelIncomplet = () => {
    return Object.values(statsAnnuelles?.par_eleve || {})
      .filter((bilan) => bilan && !bilan.complet)
      .sort((a, b) => `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, 'fr'));
  };

  const formatRangAnnuel = (bilan) => {
    const position = bilan?.classement_annuel?.position;
    const total = bilan?.classement_annuel?.total;
    if (!position || !total) return '-';
    return `${position}${position === 1 ? 'er' : 'ème'} / ${total}`;
  };

  // Handlers existants
  const handleNoteChange = (eleveId, matiereId, index, value) => {
    setNotesData(prev => {
      const newData = { ...prev };
      if (!newData[eleveId]) newData[eleveId] = {};
      if (!newData[eleveId][matiereId]) {
        newData[eleveId][matiereId] = {
          notes: Array(MAX_NOTES).fill(null),
          appreciation: ''
        };
      }
      newData[eleveId][matiereId].notes[index] = value ? parseFloat(value) : null;
      newData[eleveId][matiereId].moyenne = calculerMoyenne(newData[eleveId][matiereId].notes);
      return newData;
    });
  };

  const handleAppreciationChange = (eleveId, matiereId, value) => {
    setNotesData(prev => {
      const newData = { ...prev };
      if (!newData[eleveId]) newData[eleveId] = {};
      if (!newData[eleveId][matiereId]) {
        newData[eleveId][matiereId] = { notes: Array(MAX_NOTES).fill(null), appreciation: '' };
      }
      newData[eleveId][matiereId].appreciation = value;
      return newData;
    });
  };

  // Nouveau handler pour appréciations générales
  const handleAppreciationGeneraleChange = (eleveId, field, value) => {
    setAppreciationsGenerales(prev => ({
      ...prev,
      [eleveId]: {
        ...prev[eleveId],
        [field]: value
      }
    }));
  };

  
  // Handler de sauvegarde amélioré
 const handleSave = async () => {
  try {
    if (!annee_scolaire_id) {
      setToast({ show: true, message: '❌ Année scolaire non définie', type: 'error' });
      return;
    }
    
    setSaving(true);

    // 1. Formater les notes
    const formattedData = [];
    
    Object.entries(notesData).forEach(([eleveId, matiereNotes]) => {
      Object.entries(matiereNotes).forEach(([matiereId, data]) => {
        if (data.notes && data.notes.some(note => note !== null)) {
          const cleanedNotes = data.notes.map(note => 
            note === '' || note === null ? null : parseFloat(note)
          );
          
          formattedData.push({
            eleve_id: parseInt(eleveId),
            matiere_id: parseInt(matiereId),
            notes: JSON.stringify(cleanedNotes),
            moyenne: data.moyenne ? data.moyenne.toString() : '0',
            appreciation: data.appreciation || '',
            trimestre: trimestre.toString(),
            annee_scolaire_id: annee_scolaire_id.toString()
          });
        }
      });
    });

    // 2. Formater les appréciations trimestrielles
    const formattedAppreciations = eleves.map(eleve => {
      const classement = calculerClassement(eleve.id);
      return {
        eleve_id: eleve.id,
        trimestre: trimestre.toString(),
        annee_scolaire_id: annee_scolaire_id.toString(),
        moyenne_generale: parseFloat(calculerMoyenneGenerale(eleve.id) || 0),
        total_coefficient: 0,
        moyenne_plus_elevee_classe: parseFloat(statsClasse.moyenne_plus_elevee || 0),
        moyenne_plus_basse_classe: parseFloat(statsClasse.moyenne_plus_basse || 0),
        classement_position: classement.position,
        classement_total: classement.total,
        appreciation_conseil_classe: appreciationsGenerales[eleve.id]?.appreciation_conseil_classe || '',
        appreciation_directeur: appreciationsGenerales[eleve.id]?.appreciation_directeur || '',
        observation_parents: appreciationsGenerales[eleve.id]?.observation_parents || ''
      };
    });
  
    console.log('📝 Données notes envoyées:', formattedData);
    console.log('💬 Données appréciations envoyées:', formattedAppreciations);
    
    // 3. Enregistrer les notes
    const response = await api.scolarite.saveNotes(formattedData);
    
    if (response.success) {
      // 4. Enregistrer les appréciations
      const appResponse = await api.scolarite.saveAppreciationsTrimestrielles(
        formattedAppreciations
      );
      
      if (!appResponse.success) {
        console.warn('⚠️ Erreur sauvegarde appréciations:', appResponse.error);
      }
      
      // 5. Notifier le parent (si callback existe)
      onSave && onSave(formattedData);
      
      // 6. Attendre un peu avant de recharger
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 7. Recharger les notes complètes
      const result = await api.scolarite.getNotesComplet(
        classeId, 
        annee_scolaire_id
      );
      
      console.log('🔄 Rechargement après save:', result);
      
      if (result.success && result.data) {
        // Destructurer pour récupérer toutes les données
        const { notes, stats, appreciations, classe_info, enseignants, annuel } = result.data;
        
        // Mettre à jour notesEnrichies (tous trimestres)
        setNotesEnrichies(notes);
        setClasseInfo(classe_info);
        
        // Mettre à jour les stats du trimestre actuel
        window.statsParTrimestre = stats;
        window.statsAnnuellesClasse = annuel || { par_eleve: {}, stats_classe: null };
        setStatsAnnuelles(window.statsAnnuellesClasse);
        if (stats[trimestre]) {
          setStatsClasse(stats[trimestre]);
        }
        
        // Mettre à jour les appréciations
        window.toutesLesAppreciations = appreciations;
        
        // Filtrer par trimestre avant de recharger notesData
        const notesDuTrimestre = notes.filter(
          note => parseInt(note.trimestre) === parseInt(trimestre)
        );
        
        // Recharger notesData avec les notes verrouillées DU TRIMESTRE
        const prefilledData = {};
        notesDuTrimestre.forEach(note => {
          if (!prefilledData[note.eleve_id]) {
            prefilledData[note.eleve_id] = {};
          }

          prefilledData[note.eleve_id][note.matiere_id] = {
            notes: parseNotesSecure(note.notes, note.eleve_id, note.matiere_id),
            moyenne: note.moyenne,
            appreciation: note.appreciation || ''
          };
        });
        
        setNotesData(prefilledData);
        
        console.log('✅ States mis à jour après save');
      }
      
      setToast({ 
        show: true, 
        message: '✅ Notes et appréciations enregistrées avec succès !', 
        type: 'success' 
      });
    } else {
      throw new Error(response.error || 'Erreur lors de l\'enregistrement');
    }
  } catch (error) {
    console.error('❌ Erreur sauvegarde:', error);
    setToast({ 
      show: true, 
      message: `❌ ${error.message}`, 
      type: 'error' 
    });
  } finally {
    setSaving(false);
  }
};





// 💾 Sauvegarde individuelle d'un élève (Collège/Lycée)
const handleSaveEleve = async (eleveId) => {
  try {
    if (!annee_scolaire_id || !trimestre) {
      setToast({ show: true, message: 'Paramètres manquants', type: 'error' });
      return;
    }

    const eleve = eleves.find(e => e.id === eleveId);
    const prenomNom = `${eleve?.prenom || ''} ${eleve?.nom || ''}`.trim();

    setSavingEleve(eleveId);
    setToast({ show: true, message: `Enregistrement des notes de ${prenomNom}...`, type: 'loading' });

    // Laisser le toast loading s'afficher
    await new Promise(resolve => setTimeout(resolve, 100));

    // Préparer les notes de CET élève uniquement
    const notesEleve = [];
    const matieresAUtiliser = matieresDynamiques.length > 0 ? matieresDynamiques : matieres;

    matieresAUtiliser.forEach(matiere => {
      const data = notesData[eleveId]?.[matiere.id];
      if (data && data.notes && data.notes.some(n => n !== null && n !== '')) {
        const cleanedNotes = data.notes.map(n =>
          n === '' || n === null ? null : parseFloat(n)
        );
        notesEleve.push({
          matiere_id: matiere.id,
          notes: JSON.stringify(cleanedNotes),
          moyenne: data.moyenne ? data.moyenne.toString() : '0',
          appreciation: data.appreciation || ''
        });
      }
    });

    if (notesEleve.length === 0) {
      setToast({ show: true, message: `Aucune note à enregistrer pour ${prenomNom}`, type: 'error' });
      setSavingEleve(null);
      return;
    }

    const result = await api.scolarite.saveNotesCollegeEleve({
      eleve_id: eleveId,
      trimestre: trimestre.toString(),
      annee_scolaire_id: annee_scolaire_id.toString(),
      notes: notesEleve,
      appreciation_conseil_classe: appreciationsGenerales[eleveId]?.appreciation_conseil_classe || '',
      appreciation_directeur: appreciationsGenerales[eleveId]?.appreciation_directeur || '',
      observation_parents: appreciationsGenerales[eleveId]?.observation_parents || ''
    });

    if (result.success) {
      // Recharger les données complètes
      await new Promise(resolve => setTimeout(resolve, 500));

      const reloadResult = await api.scolarite.getNotesComplet(classeId, annee_scolaire_id);

      if (reloadResult.success && reloadResult.data) {
        const { notes, stats, appreciations, classe_info, enseignants, matieres: matieresBackend, annuel } = reloadResult.data;

        setNotesEnrichies(notes);
        setClasseInfo(classe_info);

        if (matieresBackend?.length > 0) setMatieresDynamiques(matieresBackend);

        if (enseignants?.length > 0) {
          const enseignantsMap = {};
          enseignants.forEach(ens => { enseignantsMap[ens.matiere_id] = ens.professeur_nom; });
          setEnseignantsParMatiere(enseignantsMap);
        }

        window.statsParTrimestre = stats;
        window.statsAnnuellesClasse = annuel || { par_eleve: {}, stats_classe: null };
        setStatsAnnuelles(window.statsAnnuellesClasse);
        if (stats[trimestre]) setStatsClasse(stats[trimestre]);

        window.toutesLesAppreciations = appreciations;

        // Filtrer par trimestre
        const notesDuTrimestre = notes.filter(n => parseInt(n.trimestre) === parseInt(trimestre));

        const prefilledData = {};
        notesDuTrimestre.forEach(note => {
          if (!prefilledData[note.eleve_id]) prefilledData[note.eleve_id] = {};
          prefilledData[note.eleve_id][note.matiere_id] = {
            notes: parseNotesSecure(note.notes, note.eleve_id, note.matiere_id),
            moyenne: note.moyenne,
            appreciation: note.appreciation || ''
          };
        });
        setNotesData(prefilledData);

        const appreciationsDuTrimestre = appreciations.filter(
          app => parseInt(app.trimestre) === parseInt(trimestre)
        );
        const prefilledAppreciationsGenerales = {};
        appreciationsDuTrimestre.forEach(app => {
          prefilledAppreciationsGenerales[app.eleve_id] = {
            appreciation_conseil_classe: app.appreciation_conseil_classe || '',
            appreciation_directeur: app.appreciation_directeur || '',
            observation_parents: app.observation_parents || ''
          };
        });
        setAppreciationsGenerales(prefilledAppreciationsGenerales);
      }

      setToast({
        show: true,
        message: `Notes de ${prenomNom} enregistrées et stats recalculées !`,
        type: 'success'
      });
    } else {
      throw new Error(result.error || 'Erreur');
    }

  } catch (error) {
    console.error('Erreur save individuel:', error);
    setToast({ show: true, message: ` ${error.message}`, type: 'error' });
  } finally {
    setSavingEleve(null);
  }
};



  // Composant StatutBadge
  const StatutBadge = ({ statut }) => {
    const config = {
      complete: {
        icon: CheckCircle,
        text: 'Complet',
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
        borderColor: 'border-green-300'
      },
      'complete-locked': {
        icon: Lock,
        text: 'Complet et Verrouillé',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        borderColor: 'border-blue-300'
      },
      partial: {
        icon: AlertCircle,
        text: 'En cours',
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-700',
        borderColor: 'border-orange-300'
      },
      empty: {
        icon: Circle,
        text: 'Vide',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-600',
        borderColor: 'border-gray-300'
      }
    };
    
    const { icon: Icon, text, bgColor, textColor, borderColor } = config[statut];

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${bgColor} ${textColor} ${borderColor}`}>
        <Icon size={14} />
        {text}
      </span>
    );
  };



  // Fonction pour télécharger le bulletin d'un élève
  const telechargerBulletin = async (eleveId) => {
    try {
      setToast({ show: true, message: 'Génération du bulletin en cours...', type: 'loading' });
      
      console.log('📄 Demande de génération pour élève:', eleveId);
      
      // Appeler l'API pour générer le PDF
      const pdfBlob = await api.scolarite.genererBulletinCollege(
        eleveId,
        trimestre,
        annee_scolaire_id,
        classeId
      );
      
      console.log('📄 Blob reçu:', pdfBlob.size, 'bytes');
      
      // Vérifier que c'est bien un PDF
      if (!pdfBlob || pdfBlob.size === 0) {
        throw new Error('Le fichier reçu est vide');
      }
      
      // Trouver le nom de l'élève
      const eleve = eleves.find(e => e.id === eleveId);
      const filename = `bulletin-${eleve.nom}-${eleve.prenom}-T${trimestre}.pdf`;
      
      console.log('📄 Création du lien de téléchargement:', filename);
      
      // Créer un blob explicitement en PDF
      const pdfBlobFixed = new Blob([pdfBlob], { type: 'application/pdf' });
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(pdfBlobFixed);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer après un délai
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      setToast({ 
        show: true, 
        message: 'Bulletin téléchargé avec succès !', 
        type: 'success' 
      });
      
    } catch (error) {
      console.error('❌ Erreur téléchargement bulletin:', error);
      setToast({ 
        show: true, 
        message: `❌ Erreur: ${error.message}`, 
        type: 'error' 
      });
    }
  };


  // Fonction pour télécharger tous les bulletins
  const telechargerTousBulletins = async () => {
    try {
      const totalEleves = eleves.length;
      const tempsEstimeSecondes = Math.ceil(totalEleves * 2.5);
      const tempsEstimeMinutes = Math.ceil(tempsEstimeSecondes / 60);
      
      const messageTemps = tempsEstimeMinutes >= 2 
        ? `environ ${tempsEstimeMinutes} minutes`
        : `environ 1 minute`;
      
      setToast({ 
        show: true, 
        message: `Génération de ${totalEleves} bulletins en cours... Patience, ${messageTemps}.`, 
        type: 'loading' 
      });
      
      console.log('📦 Demande de génération de tous les bulletins');
      
      // Appeler l'API pour générer le ZIP
      const zipBlob = await api.scolarite.genererTousBulletinsCollege(
        classeId,
        trimestre,
        annee_scolaire_id
      );
      
      console.log('📦 ZIP reçu:', zipBlob.size, 'bytes');
      
      // Créer un nom de fichier
      const classe = classeInfo?.nom || 'classe';
      const filename = `bulletins-${classe}-T${trimestre}.zip`;
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer après un délai
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      setToast({ 
        show: true, 
        message: `${totalEleves} bulletins téléchargés avec succès !`, 
        type: 'success' 
      });
      
    } catch (error) {
      console.error('❌ Erreur téléchargement bulletins:', error);
      setToast({ 
        show: true, 
        message: `❌ Erreur lors de la génération: ${error.message}`, 
        type: 'error' 
      });
    }
  };

  const AutoAppreciationBadge = ({ moyenne }) => {
  if (!moyenne || moyenne === '-' || moyenne === '0') return null;
  const moyValue = parseFloat(moyenne);
  if (isNaN(moyValue) || moyValue === 0) return null;
  const app = getAppreciation(moyValue, 'note');
  if (!app || app.libelle === '-') return null;
  return (
    <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${app.couleur}`}>
      {app.libelle}
    </span>
  );
};


// Loader
  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#246990]"></div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 relative">
      {/* Loader de sauvegarde */}
      {saving && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#246990]"></div>
            <p className="text-lg font-semibold text-gray-700">Enregistrement en cours...</p>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-[#246990] to-[#3084b5] rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              Bulletin Collège/Lycée - Trimestre {trimestre}
            </h2>
            <p className="text-blue-100 mt-1">{eleves.length} élèves • {matieres.length} matières</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Bouton Modification */}
            <div className="relative group">
              <button
                onClick={() => setModificationEnabled(!modificationEnabled)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  modificationEnabled
                    ? 'bg-[#319190]/90 text-white hover:bg-[#319190]'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {modificationEnabled ? <Unlock size={18} /> : <Lock size={18} />}
                {modificationEnabled ? 'Modification activée' : 'Modification verrouillée'}
              </button>
              
              {/* Tooltip */}
              <div className="absolute left-0 top-full mt-2 w-96 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-3 hidden group-hover:block z-50 border border-blue-200">
                <div className="flex items-start gap-2">
                  <Lock className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-blue-700 mb-1">Protection des modifications</p>
                    <p className="text-gray-600">
                      Ce bouton permet d'activer ou désactiver la modification des notes <span className="font-semibold">non encore enregistrées</span>. 
                      Les notes déjà verrouillées (en rouge) <span className="font-semibold text-red-600">ne peuvent jamais être modifiées</span> sans autorisation de l'administrateur.
                    </p>
                  </div>
                </div>
                <div className="absolute -top-2 left-8 w-4 h-4 bg-white border-l border-t border-blue-200 transform rotate-45"></div>
              </div>
            </div>

            <button
            onClick={telechargerTousBulletins}
            className="flex items-center gap-2 px-4 py-2 bg-white text-[#246990] rounded-lg font-medium hover:bg-blue-50 transition-all"
          >
            <Download size={18} />
            Tous les bulletins
          </button>

          </div>
        </div>
      </div>

      {/* Stats de classe */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-[#319190]/10 to-[#319190]/30 rounded-xl p-4 border border-[#319190]/40">
          <div className="flex items-center gap-2 text-sm font-medium text-[#319190]/90">
            <div className="bg-[#319190]/90 p-2 rounded-full">
              <ThumbsUp size={16} className="text-white" />
            </div>
            Moyenne la plus élevée
          </div>
          <div className="text-3xl font-bold text-[#319190]/90 mt-1">
            {statsClasse.moyenne_plus_elevee || '-'}
          </div>
          {statsClasse.eleve_moyenne_plus_elevee && (
            <div className="mt-3 inline-block">
              <div className="bg-white px-3 py-2 rounded-lg shadow-md border border-[#319190]/20">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#319190] rounded-full"></div>
                  <span className="text-sm font-bold text-gray-800">
                    {statsClasse.eleve_moyenne_plus_elevee}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-[#ae7467]/10 to-[#ae7467]/30 rounded-xl p-4 border border-[#ae7467]/40">
          <div className="flex items-center gap-2 text-sm font-medium text-[#ae7467]/90">
            <div className="bg-[#ae7467]/90 p-2 rounded-full">
              <ThumbsDown size={16} className="text-white" />
            </div>
            Moyenne la plus basse
          </div>
          <div className="text-3xl font-bold text-[#ae7467]/90 mt-1">
            {statsClasse.moyenne_plus_basse || '-'}
          </div>
          {statsClasse.eleve_moyenne_plus_basse && (
            <div className="mt-3 inline-block">
              <div className="bg-white px-3 py-2 rounded-lg shadow-md border border-[#ae7467]/20">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#ae7467] rounded-full"></div>
                  <span className="text-sm font-bold text-gray-800">
                    {statsClasse.eleve_moyenne_plus_basse}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {isTroisiemeTrimestre && (() => {
        const stats = statsAnnuelles?.stats_classe || {};
        const hasAnnualStats = Number(stats.total_eleves_bilan_complet || 0) > 0;
        const elevesBilanIncomplet = getElevesBilanAnnuelIncomplet();

        return (
          <div className="bg-white rounded-xl shadow-sm border border-[#246990]/15 p-4">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-sm font-bold text-[#246990] uppercase tracking-wide">Bilan annuel de la classe</h3>
                <p className="text-xs text-gray-500 mt-1">Calculé sur les moyennes générales des trimestres 1, 2 et 3.</p>
              </div>
              <span className="text-xs font-semibold text-[#319190] bg-[#319190]/10 px-3 py-1 rounded-full">
                {stats.total_eleves_bilan_complet || 0} bilan{Number(stats.total_eleves_bilan_complet || 0) > 1 ? 's' : ''} complet{Number(stats.total_eleves_bilan_complet || 0) > 1 ? 's' : ''}
              </span>
            </div>

            {hasAnnualStats ? (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="rounded-lg border border-[#319190]/20 bg-[#319190]/5 p-3">
                  <p className="text-xs font-medium text-gray-600">Meilleure moyenne annuelle</p>
                  <p className="text-2xl font-bold text-[#319190] mt-1">{formatNoteAnnuelle(stats.moyenne_plus_elevee)}<span className="text-sm text-gray-500">/20</span></p>
                  <p className="text-xs font-semibold text-gray-700 mt-2 truncate">{stats.eleve_moyenne_plus_elevee || '-'}</p>
                </div>
                <div className="rounded-lg border border-[#ae7467]/20 bg-[#ae7467]/5 p-3">
                  <p className="text-xs font-medium text-gray-600">Moyenne annuelle la plus basse</p>
                  <p className="text-2xl font-bold text-[#ae7467] mt-1">{formatNoteAnnuelle(stats.moyenne_plus_basse)}<span className="text-sm text-gray-500">/20</span></p>
                  <p className="text-xs font-semibold text-gray-700 mt-2 truncate">{stats.eleve_moyenne_plus_basse || '-'}</p>
                </div>
                <div className="rounded-lg border border-[#246990]/20 bg-[#246990]/5 p-3">
                  <p className="text-xs font-medium text-gray-600">Moyenne annuelle de la classe</p>
                  <p className="text-2xl font-bold text-[#246990] mt-1">{formatNoteAnnuelle(stats.moyenne_classe_annuelle)}<span className="text-sm text-gray-500">/20</span></p>
                  <p className="text-xs text-gray-500 mt-2">Élèves avec bilan complet</p>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-medium text-gray-600">Dossiers incomplets</p>
                      <p className="text-2xl font-bold text-amber-700 mt-1">{stats.total_eleves_incomplets || 0}</p>
                    </div>
                    {elevesBilanIncomplet.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowElevesDossiersIncomplets((prev) => !prev)}
                        className="h-8 w-8 rounded-full border border-amber-200 bg-white text-amber-700 hover:bg-amber-100 flex items-center justify-center transition-colors"
                        aria-label={showElevesDossiersIncomplets ? 'Masquer les dossiers incomplets' : 'Voir les dossiers incomplets'}
                      >
                        {showElevesDossiersIncomplets ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">T1, T2 ou T3 manquant</p>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                <div>Données annuelles incomplètes : aucun élève n'a encore les trois moyennes trimestrielles enregistrées.</div>
                {elevesBilanIncomplet.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowElevesDossiersIncomplets((prev) => !prev)}
                    className="mt-3 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors"
                  >
                    Voir les {elevesBilanIncomplet.length} élève{elevesBilanIncomplet.length > 1 ? 's' : ''} concerné{elevesBilanIncomplet.length > 1 ? 's' : ''}
                    {showElevesDossiersIncomplets ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                )}
              </div>
            )}

            {elevesBilanIncomplet.length > 0 && showElevesDossiersIncomplets && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/70 p-3">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="text-sm font-semibold text-amber-800">Élèves avec dossier annuel incomplet</p>
                  <span className="text-xs font-bold text-amber-700 bg-white border border-amber-200 rounded-full px-2 py-0.5">
                    {elevesBilanIncomplet.length}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
                  {elevesBilanIncomplet.map((bilan) => {
                    const trimestresManquants = getTrimestresBilanManquants(bilan).map((trimestreKey) => `T${trimestreKey}`).join(', ');
                    return (
                      <div key={bilan.eleve_id} className="rounded-md border border-amber-100 bg-white px-3 py-2">
                        <p className="text-sm font-semibold text-gray-800 truncate">{bilan.nom} {bilan.prenom}</p>
                        <p className="text-xs text-amber-700 mt-1">Manquant : {trimestresManquants || '-'}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Alerte notes verrouillées */}
    {notesEnrichies.filter(n => parseInt(n.trimestre) === parseInt(trimestre)).some(n => n.verrouille === 1) && (
    <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-4 flex items-start gap-3">
          <Lock size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">
              Certaines notes sont verrouillées
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Les notes déjà enregistrées sont automatiquement verrouillées. Pour toute modification, veuillez contacter votre administrateur.
            </p>
          </div>
        </div>
      )}

      {/* Vérification données */}
      {!eleves || !matieres || eleves.length === 0 || matieres.length === 0 ? (
        <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-xl">
          <p>Aucun élève ou matière disponible pour cette classe.</p>
        </div>
      ) : (

        <div className="space-y-3">
          {/* Recherche d'élève */}
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Rechercher un élève par nom..."
              value={eleveSearchTerm}
              onChange={(e) => setEleveSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20 outline-none transition-all"
            />
          </div>

        
          {/* ============ BANDEAU COMPLÉTUDE ============ */}
          {(() => {
            const matieresAUtiliser = matieresDynamiques.length > 0 ? matieresDynamiques : matieres;
            const totalMatieres = matieresAUtiliser.length;
            
            let complets = 0;
            let incomplets = 0;
            let vides = 0;
            const incompletsList = [];
            const videsList = [];

            eleves.forEach(eleve => {
              let count = 0;
              const manquantes = [];
              
              matieresAUtiliser.forEach(matiere => {
                const notes = notesData[eleve.id]?.[matiere.id]?.notes || [];
                if (notes.some(n => n !== null && n !== '')) {
                  count++;
                } else {
                  manquantes.push(matiere.nom);
                }
              });

              if (count === 0) {
                vides++;
                videsList.push({ nom: `${eleve.nom} ${eleve.prenom}`, manquantes });
              } else if (count < totalMatieres) {
                incomplets++;
                incompletsList.push({ nom: `${eleve.nom} ${eleve.prenom}`, manquantes, count, total: totalMatieres });
              } else {
                complets++;
              }
            });

            if (eleves.length === 0 || totalMatieres === 0) return null;

            return (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileCheck size={18} className="text-[#246990]" />
                  <span className="text-sm font-bold text-gray-700">Complétude des bulletins</span>
                  <span className="text-xs text-gray-400">({totalMatieres} matières attendues)</span>
                </div>
                
                {/* Barre de progression */}
                <div className="flex rounded-full overflow-hidden h-3 mb-3">
                  {complets > 0 && (
                    <div className="bg-green-500 transition-all" style={{ width: `${(complets / eleves.length) * 100}%` }} 
                      title={`${complets} complet${complets > 1 ? 's' : ''}`} />
                  )}
                  {incomplets > 0 && (
                    <div className="bg-orange-400 transition-all" style={{ width: `${(incomplets / eleves.length) * 100}%` }}
                      title={`${incomplets} incomplet${incomplets > 1 ? 's' : ''}`} />
                  )}
                  {vides > 0 && (
                    <div className="bg-gray-300 transition-all" style={{ width: `${(vides / eleves.length) * 100}%` }}
                      title={`${vides} vide${vides > 1 ? 's' : ''}`} />
                  )}
                </div>

                {/* Compteurs */}
                <div className="flex flex-wrap gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-gray-600">Complets :</span>
                    <span className="font-bold text-green-700">{complets}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-orange-400"></div>
                    <span className="text-gray-600">Incomplets :</span>
                    <span className="font-bold text-orange-700">{incomplets}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                    <span className="text-gray-600">Vides :</span>
                    <span className="font-bold text-gray-600">{vides}</span>
                  </div>
                </div>

                {/* Détails incomplets */}
                {incomplets > 0 && (
                  <details className="mt-3 border-t border-gray-100 pt-3">
                    <summary className="text-xs font-semibold text-orange-600 cursor-pointer flex items-center gap-1">
                      <FileWarning size={14} />
                      {incomplets} bulletin{incomplets > 1 ? 's' : ''} incomplet{incomplets > 1 ? 's' : ''}, cliquez pour voir
                    </summary>
                    <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                      {incompletsList.map((item, i) => (
                        <div key={i} className="bg-orange-50 rounded-lg p-2 text-xs border border-orange-200">
                          <span className="font-semibold text-gray-700">{item.nom}</span>
                          <span className="text-orange-600 ml-2">({item.count}/{item.total})</span>
                          <div className="text-orange-500 mt-1">
                            Manque : {item.manquantes.join(', ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}

                {/* Détails vides */}
                {vides > 0 && (
                  <details className="mt-3 border-t border-gray-100 pt-3">
                    <summary className="text-xs font-semibold text-gray-500 cursor-pointer flex items-center gap-1">
                      <FileX size={14} />
                      {vides} bulletin{vides > 1 ? 's' : ''} vide{vides > 1 ? 's' : ''}, cliquez pour voir
                    </summary>
                    <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                      {videsList.map((item, i) => (
                        <div key={i} className="bg-gray-50 rounded-lg p-2 text-xs border border-gray-200">
                          <span className="font-semibold text-gray-700">{item.nom}</span>
                          <span className="text-gray-400 ml-2">(aucune note)</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            );
          })()}


          {/* Liste des élèves */}
          {eleves
            .filter(eleve => {
              if (!eleveSearchTerm.trim()) return true;
              const search = eleveSearchTerm.toLowerCase().trim();
              return `${eleve.nom} ${eleve.prenom}`.toLowerCase().includes(search);
            })
            .map((eleve) => {


            const isOpen = openEleves[eleve.id];
            const statut = getStatutEleve(eleve.id);
            const moyenne = calculerMoyenneGenerale(eleve.id);
            const classement = calculerClassement(eleve.id);
            const bilanAnnuel = getBilanAnnuelEleve(eleve.id);

            return (
              <div key={eleve.id} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
                {/* Header de l'élève (cliquable) */}
                <div
                  onClick={() => toggleEleve(eleve.id)}
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <button className="text-[#246990] hover:text-[#3084b5] transition-colors">
                      {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                    </button>
                    <div>
                      <h3 className="text-lg font-bold text-[#246990]/90">
                        {eleve.nom} {eleve.prenom}
                      </h3>

                      <div className="flex items-center gap-3 text-sm text-gray-900">
                      <span>Moyenne: {moyenne || '-'}</span>
                      <span>•</span>
                      <div className="relative group inline-flex items-center gap-1">
                        <span>
                          Classement: {classement.position === 1 ? '1er' : `${classement.position}ème`} / {classement.total}
                        </span>
                        <AlertCircle className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                        
                        {/* Tooltip Classement */}
                        <div className="absolute left-0 top-full mt-2 w-80 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-3 hidden group-hover:block z-50 border border-blue-200">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-blue-700 mb-1">Classement général</p>
                              <p className="text-gray-600">
                                Le classement indique la <span className="font-semibold">position globale de l'élève</span> basée sur sa moyenne générale de toutes les matières. 
                                Cela permet de voir sa performance d'ensemble par rapport à ses camarades.
                              </p>
                            </div>
                          </div>
                          <div className="absolute -top-2 left-8 w-4 h-4 bg-white border-l border-t border-blue-200 transform rotate-45"></div>
                        </div>
                      </div>
                    </div>

                    </div>
                  </div>

               
                <div className="flex items-center gap-2">
                  <StatutBadge statut={statut} />
                  
                  {/* 💾 Bouton sauvegarde individuelle */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveEleve(eleve.id);
                    }}
                    disabled={savingEleve === eleve.id || !modificationEnabled || statut === 'complete-locked'}
                    className={`p-2 rounded-lg transition-all ${
                      statut === 'complete-locked'
                        ? 'text-gray-300 cursor-not-allowed opacity-40'
                        : savingEleve === eleve.id
                        ? 'text-gray-400 cursor-wait'
                        : !modificationEnabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-[#319190] hover:bg-[#319190]/10'
                    }`}
                    title={
                      statut === 'complete-locked'
                        ? 'Bulletin complet et verrouillé'
                        : !modificationEnabled
                        ? 'Activez la modification pour sauvegarder'
                        : `Enregistrer les notes de ${eleve.prenom}`
                    }
                  >
                    <Save size={20} />
                  </button>

                  {/* 📥 Bouton téléchargement bulletin */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      telechargerBulletin(eleve.id);
                    }}
                    className="p-2 text-[#246990] hover:bg-blue-50 rounded-lg transition-colors"
                    title={`Télécharger le bulletin de ${eleve.prenom}`}
                  >
                    <Download size={20} />
                  </button>
                </div>

                </div>

                {/* Contenu détaillé (collapse) */}
                {isOpen && (
                  <div className="p-4 bg-gray-50 border-t border-gray-200">
                    <div className="space-y-4">
                      {/* Tableau des notes */}
                      <div className="bg-white rounded-lg overflow-hidden shadow-sm">
                        <table className="w-full">
                          <thead className="bg-gradient-to-r from-[#319190] to-[#63b5b4] text-white">
                            <tr>
                              <th className="py-3 px-4 text-left font-medium">Discipline</th>
                              <th className="py-3 px-4 text-center font-medium w-20">Coef.</th>
                              <th className="py-3 px-4 text-left font-medium w-40">Enseignant</th>
                              <th className="py-3 px-4 text-center font-medium w-24">Eval. Classe</th>
                              <th className="py-3 px-4 text-center font-medium w-24">Eval. Dép.</th>
                              <th className="py-3 px-4 text-center font-medium w-24">Compo.</th>
                              <th className="py-3 px-4 text-center font-medium w-20">Moy.</th>
                              <th className="py-3 px-4 text-center font-medium w-20">
                              <div className="flex items-center justify-center gap-1 relative group">
                                Rang
                                <AlertCircle className="w-4 h-4 cursor-help" />
                                
                                {/* Tooltip Rang */}
                                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-80 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-3 hidden group-hover:block z-50 border border-yellow-200">
                                  <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                      <p className="font-semibold text-yellow-700 mb-1">Rang par matière</p>
                                      <p className="text-gray-600">
                                        Le rang indique la <span className="font-semibold">position de l'élève dans cette matière spécifique</span> par rapport à ses camarades. 
                                        Par exemple, "2/49" signifie qu'il est 2ème sur 49 élèves en Anglais.
                                      </p>
                                    </div>
                                  </div>
                                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-yellow-200 transform rotate-45"></div>
                                </div>
                              </div>
                            </th>
                              <th className="py-3 px-4 text-left font-medium w-48">Appréciation</th>
                              <th className="py-3 px-4 text-center font-medium w-40">Modifié par</th>
                            </tr>
                          </thead>
                          <tbody>

                           {(matieresDynamiques.length > 0 ? matieresDynamiques : matieres).map((matiere) => {
  
                            const infosEnrichies = getInfosEnrichies(eleve.id, matiere.id);
                            const isNoteVerrouillee = infosEnrichies.verrouille === 1;
                            
                            // ⬅️ AJOUTER ces logs pour debug
                            if (matiere.nom === 'ANGLAIS' && eleve.nom === 'KABONGO') {  // Remplace par un nom d'élève de Seconde A
                              console.log('🔍 DEBUG COEFFICIENT ANGLAIS:', {
                                eleve: `${eleve.nom} ${eleve.prenom}`,
                                matiere: matiere.nom,
                                coefficient_matiere_props: matiere.coefficient,
                                coefficient_enrichi: infosEnrichies.coefficient,
                                coefficient_affiche: infosEnrichies.coefficient || matiere.coefficient || '-',
                                infosEnrichies_complete: infosEnrichies
                              });
                            }
                            
                            return (
                              <tr key={matiere.id} className="border-b border-gray-100 hover:bg-gray-50">
                                {/* Matière */}
                                <td className="py-3 px-4 font-medium text-gray-700">
                                  <div className="flex items-center gap-2">
                                    {matiere.nom}
                                    {infosEnrichies.is_transferred === 1 && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-100 text-teal-700 border border-teal-200" title={`Note transférée depuis ${infosEnrichies.source_site}`}>
                                        <Network size={10} />
                                        Site source
                                      </span>
                                    )}
                                  </div>
                                </td>
                                

                                {/* Coefficient */}
                                <td className="py-3 px-4 text-center">
                                  <div className="flex items-center justify-center gap-2">
                                    <span className="inline-block bg-[#246990] text-white px-2 py-1 rounded text-xs font-bold">
                                      {matiere.coefficient || '-'}
                                    </span>
                                  
                                  </div>
                                </td>

                                  {/* Enseignant */}
                                <td className="py-3 px-4">
                                  <div className="text-sm text-gray-600 italic">
                                    {infosEnrichies.professeur_nom || enseignantsParMatiere[matiere.id] || 'Non assigné'}
                                  </div>
                                </td>
                                                                  
                                  {/* 3 colonnes de notes */}
                                  {Array(MAX_NOTES).fill(null).map((_, index) => {
                                    let columnLabel;
                                    switch(index) {
                                      case 0: columnLabel = "Evaluation de classe"; break;
                                      case 1: columnLabel = "Evaluation départementale"; break;
                                      case 2: columnLabel = "Composition"; break;
                                      default: columnLabel = `Note ${index + 1}`;
                                    }
                                    return (
                                      <td key={index} className="py-3 px-4">
                                        <div className="relative">
                                          <input
                                            type="number"
                                            min="0"
                                            max="20"
                                            step="0.25"
                                            disabled={!modificationEnabled || isNoteVerrouillee}
                                            className={`w-16 p-2 border rounded text-center ${
                                              isNoteVerrouillee
                                                ? 'bg-red-50 border-red-300 cursor-not-allowed'
                                                : modificationEnabled
                                                ? 'border-[#246990] focus:ring-2 focus:ring-[#246990]'
                                                : 'bg-gray-100 cursor-not-allowed'
                                            }`}
                                            value={notesData[eleve.id]?.[matiere.id]?.notes[index] || ''}
                                            onChange={(e) => handleNoteChange(eleve.id, matiere.id, index, e.target.value)}
                                            title={isNoteVerrouillee ? "Note verrouillée - Contactez votre administrateur" : columnLabel}
                                          />
                                          {isNoteVerrouillee && (
                                            <div className="flex items-center gap-1 mt-1">
                                              <Lock size={10} className="text-red-500" />
                                              <span className="text-[10px] text-red-600 font-medium">Verrouillée</span>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    );
                                  })}
                                  
                                  {/* Moyenne */}
                                  <td className="py-3 px-4 text-center font-medium text-sm">
                                    <span className="inline-block bg-gray-100 px-2 py-1 rounded">
                                      {notesData[eleve.id]?.[matiere.id]?.moyenne || '-'}
                                    </span>
                                  </td>
                                  
                                  {/* Rang */}
                                  <td className="py-3 px-4 text-center">
                                    <span className="inline-block bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-bold">
                                      {infosEnrichies.rang ? `${infosEnrichies.rang}/${infosEnrichies.total_eleves}` : '-'}
                                    </span>
                                  </td>
                                  

                                  {/* Appréciation */}
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-2">
                                      <AutoAppreciationBadge moyenne={notesData[eleve.id]?.[matiere.id]?.moyenne} />
                                      {configChamps.observation_matiere && (
                                        <input
                                          type="text"
                                          disabled={!modificationEnabled || isNoteVerrouillee}
                                          className={`flex-1 p-2 border rounded text-sm ${
                                            isNoteVerrouillee
                                              ? 'bg-red-50 border-red-300 cursor-not-allowed'
                                              : modificationEnabled
                                              ? 'border-gray-300 focus:ring-2 focus:ring-[#246990]'
                                              : 'bg-gray-100 cursor-not-allowed'
                                          }`}
                                          value={notesData[eleve.id]?.[matiere.id]?.appreciation || ''}
                                          onChange={(e) => handleAppreciationChange(eleve.id, matiere.id, e.target.value)}
                                          placeholder={isNoteVerrouillee ? "Verrouillée" : "Observation..."}
                                          title={isNoteVerrouillee ? "Note verrouillée" : ""}
                                        />
                                      )}
                                    </div>
                                  </td>
                                                                
                                  
                                  {/* Modifié par */}
                                  <td className="py-3 px-4 text-center">
                                    {infosEnrichies.enregistre_par ? (
                                      <div className="text-xs inline-block">
                                        <div className="flex items-center justify-center gap-1 text-gray-700">
                                          <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                          </svg>
                                          <span className="font-medium">{infosEnrichies.enregistre_par}</span>
                                        </div>
                                        <div className="text-gray-500 mt-0.5">
                                          {new Date(infosEnrichies.updated_at).toLocaleDateString('fr-FR', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric'
                                          })}
                                          {' '}
                                          {new Date(infosEnrichies.updated_at).toLocaleTimeString('fr-FR', {
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-400 italic">Non enregistré</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Section Appréciations Générales */}

                      <div className="bg-white rounded-lg p-4 shadow-sm">

                       {(configChamps.appreciation_responsable || configChamps.appreciation_directeur || configChamps.observation_parents) && (
                          <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                            Appréciations Générales
                          </h4>
                        )}
                        
                        {/* ⬅️ NOUVEAU : Vérifier si au moins une note est verrouillée */}
                        {(() => {
                          const hasVerrouillage = (matieresDynamiques.length > 0 ? matieresDynamiques : matieres).some(matiere => {
                            const infos = getInfosEnrichies(eleve.id, matiere.id);
                            return infos.verrouille === 1;
                          });
                          
                          return (
                            <>
                              {/* ⬅️ NOUVEAU : Alerte si verrouillé */}
                              {hasVerrouillage && (configChamps.appreciation_responsable || configChamps.appreciation_directeur || configChamps.observation_parents) && (
                               <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 flex items-start gap-2">
                                  <Lock size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                                  <div className="text-xs text-red-700">
                                    <p className="font-semibold">Appréciations verrouillées</p>
                                    <p className="mt-1">
                                      Les appréciations sont verrouillées car cet élève a au moins une note enregistrée. 
                                      Pour les modifier, contactez votre administrateur.
                                    </p>
                                  </div>
                                </div>
                              )}
                              
                              <div className="grid grid-cols-1 gap-3">
                                {[
                                  { key: 'appreciation_conseil_classe', label: 'Appréciation du Conseil de Classe', configKey: 'appreciation_responsable' },
                                  { key: 'appreciation_directeur', label: 'Appréciation du Directeur', configKey: 'appreciation_directeur' },
                                  { key: 'observation_parents', label: 'Observation des Parents', configKey: 'observation_parents' },
                                ].filter(f => configChamps[f.configKey]).map(({ key: field, label }) => (
                                  <div key={field}>
                                 <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {label}
                                </label>
                                    <textarea
                                      disabled={!modificationEnabled || hasVerrouillage}
                                      className={`w-full p-2 border rounded-lg ${
                                        hasVerrouillage
                                          ? 'bg-red-50 border-red-300 cursor-not-allowed'
                                          : modificationEnabled
                                          ? 'border-gray-300 focus:ring-2 focus:ring-[#246990]'
                                          : 'bg-gray-100 cursor-not-allowed'
                                      }`}
                                      rows="2"
                                      value={appreciationsGenerales[eleve.id]?.[field] || ''}
                                      onChange={(e) => handleAppreciationGeneraleChange(eleve.id, field, e.target.value)}
                                      placeholder={
                                        hasVerrouillage 
                                          ? 'Verrouillé' 
                                          : `Saisir ${field === 'appreciation_conseil_classe' ? "l'appréciation" : field === 'appreciation_directeur' ? "l'appréciation" : "l'observation"}...`
                                      }
                                    />
                                    
                                    {/* ⬅️ NOUVEAU : Indicateur de verrouillage */}
                                    {hasVerrouillage && (
                                      <div className="flex items-center gap-1 mt-1">
                                        <Lock size={10} className="text-red-500" />
                                        <span className="text-[10px] text-red-600 font-medium">Verrouillé</span>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </>
                          );
                        })()}

                        {isTroisiemeTrimestre && (
                          <div className="bg-[#246990]/5 rounded-lg p-4 shadow-sm border border-[#246990]/15 mt-4">
                            <div className="flex items-center justify-between gap-3 mb-3">
                              <h4 className="font-bold text-[#246990] text-sm uppercase tracking-wide">Parcours annuel</h4>
                              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${bilanAnnuel?.complet ? 'bg-[#319190]/10 text-[#319190]' : 'bg-amber-100 text-amber-700'}`}>
                                {bilanAnnuel?.complet ? 'Bilan complet' : 'Données incomplètes'}
                              </span>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                              <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
                                <p className="text-xs text-gray-500 font-medium">T1</p>
                                <p className="text-lg font-bold text-gray-800 mt-1">{formatNoteAnnuelle(bilanAnnuel?.moyennes_trimestrielles?.[1])}</p>
                              </div>
                              <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
                                <p className="text-xs text-gray-500 font-medium">T2</p>
                                <p className="text-lg font-bold text-gray-800 mt-1">{formatNoteAnnuelle(bilanAnnuel?.moyennes_trimestrielles?.[2])}</p>
                              </div>
                              <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
                                <p className="text-xs text-gray-500 font-medium">T3</p>
                                <p className="text-lg font-bold text-gray-800 mt-1">{formatNoteAnnuelle(bilanAnnuel?.moyennes_trimestrielles?.[3])}</p>
                              </div>
                              <div className="bg-white rounded-lg border border-[#319190]/20 p-3 text-center">
                                <p className="text-xs text-gray-500 font-medium">Moyenne annuelle</p>
                                <p className="text-lg font-bold text-[#319190] mt-1">{bilanAnnuel?.complet ? `${formatNoteAnnuelle(bilanAnnuel.moyenne_annuelle)}/20` : '-'}</p>
                              </div>
                              <div className="bg-white rounded-lg border border-[#246990]/20 p-3 text-center">
                                <p className="text-xs text-gray-500 font-medium">Rang annuel</p>
                                <p className="text-lg font-bold text-[#246990] mt-1">{bilanAnnuel?.complet ? formatRangAnnuel(bilanAnnuel) : '-'}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Récapitulatif Performance */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 shadow-sm border border-blue-200 mt-4">
                          <div className="grid grid-cols-2 gap-6">
                            {/* Colonne gauche : Enseignant principal */}
                            <div>
                              <p className="text-sm font-medium text-gray-600 mb-2">Enseignant principal</p>
                              <p className="text-lg font-bold text-[#246990]">
                                {classeInfo?.enseignant_nom || 'Non assigné'}
                              </p>
                            </div>

                            {/* Colonne droite : Statistiques */}
                            <div className="text-right">
                              <p className="text-sm font-medium text-gray-800 mb-2">Performance de l'élève</p>
                              <div className="space-y-1">

                              <div className="flex justify-end items-baseline gap-2 relative group">
                                <span className="text-sm text-gray-800">Moyenne générale :</span>
                                <span className="text-3xl font-bold text-[#319190]">{moyenne || '-'}</span>
                                <span className="text-sm text-gray-500">/20</span>
                                <AlertCircle className="w-4 h-4 text-gray-400 cursor-help ml-1 mb-2" />
                                
                                {/* Tooltip Calcul */}
                                <div className="absolute right-0 bottom-full mb-2 w-80 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-3 hidden group-hover:block z-50 border border-blue-200">
                                  <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                      <p className="font-semibold text-blue-700 mb-1">Calcul de la moyenne</p>
                                      <p className="text-gray-600">
                                        La moyenne générale est calculée en faisant la <span className="font-semibold">moyenne de toutes les moyennes par matière</span>.
                                      </p>
                                    </div>
                                  </div>
                                  <div className="absolute -bottom-2 right-12 w-4 h-4 bg-white border-r border-b border-blue-200 transform rotate-45"></div>
                                </div>
                              </div>

                                <div className="flex justify-end items-center gap-2 text-sm mt-2">
                                  <span className="text-gray-800">Classement :</span>
                                  <span className="font-semibold text-[#246990]">
                                    {classement.position === 1 ? '1er' : `${classement.position}ème`} sur {classement.total} élève{classement.total > 1 ? 's' : ''}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ⬅️ AJOUTER : Historique des modifications */}
                      {(() => {
                        // Récupérer toutes les notes de l'élève avec leurs employés
                        const notesAvecEmployes = matieres
                          .map(m => getInfosEnrichies(eleve.id, m.id))
                          .filter(n => n.enregistre_par);
                        
                        if (notesAvecEmployes.length === 0) {
                          return null;
                        }
                        
                        // Grouper par employé
                        const parEmploye = {};
                        notesAvecEmployes.forEach(note => {
                          if (!parEmploye[note.enregistre_par]) {
                            parEmploye[note.enregistre_par] = {
                              count: 0,
                              lastUpdate: note.updated_at
                            };
                          }
                          parEmploye[note.enregistre_par].count++;
                          if (new Date(note.updated_at) > new Date(parEmploye[note.enregistre_par].lastUpdate)) {
                            parEmploye[note.enregistre_par].lastUpdate = note.updated_at;
                          }
                        });
                        
                        return (
                          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-xs">
                            <div className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                              </svg>
                              Historique des modifications
                            </div>
                            <div className="space-y-1">
                              {Object.entries(parEmploye).map(([employe, data]) => (
                                <div key={employe} className="flex items-center justify-between text-gray-600">
                                  <span>
                                    <span className="font-medium text-gray-700">{employe}</span>
                                    <span className="text-gray-400"> • {data.count} note{data.count > 1 ? 's' : ''}</span>
                                  </span>
                                  <span className="text-gray-500">
                                    {new Date(data.lastUpdate).toLocaleDateString('fr-FR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Bouton d'enregistrement */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || !modificationEnabled}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm shadow-md transition-all ${
            saving || !modificationEnabled
              ? 'bg-gray-300 cursor-not-allowed text-gray-500'
              : 'bg-gradient-to-r from-[#246990] to-[#3084b5] text-white hover:shadow-lg hover:scale-105'
          }`}
        >
          <Save size={16} />
          {saving ? 'Enregistrement...' : 'Enregistrer les notes'}
        </button>
      </div>
    </div>
  );
};

export default TableauNotes;
